import { reaction } from 'mobx';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { useHashRoute } from '@/hooks/useHashRoute';
import { trackEvent } from '@/utils/analytics';

import { subscriptionStore } from '../subscription.store';

import { getSpendablePool } from './use-credit-usage';

const WINDOW_MS = 6 * 60 * 60 * 1000;
const BURN_TOAST_PCT = 0.2;
const STORAGE_KEY = 'usage-burn-window';

type BurnWindow = {
  windowStartedAt: number;
  usedAtWindowStart: number;
  lastToastAt: number;
};

const readBurnWindow = (): BurnWindow | null => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '');
  } catch {
    return null;
  }
};

const writeBurnWindow = (burnWindow: BurnWindow) =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(burnWindow));

export const useUsagePaceToasts = () => {
  const { setHash } = useHashRoute();

  useEffect(
    () =>
      reaction(
        () => ({
          creditLimits: subscriptionStore.state.creditLimits,
          liteModeActive: subscriptionStore.state.liteModeActive,
        }),
        ({ creditLimits, liteModeActive }) => {
          if (
            liteModeActive ||
            !subscriptionStore.isPlus ||
            !creditLimits ||
            creditLimits.allowance_credits <= 0
          )
            return;

          const now = Date.now();
          const used = creditLimits.used_credits;
          let burnWindow = readBurnWindow();
          const windowExpired =
            !burnWindow ||
            now - burnWindow.windowStartedAt > WINDOW_MS ||
            used < burnWindow.usedAtWindowStart;
          if (!burnWindow || windowExpired) {
            burnWindow = {
              windowStartedAt: now,
              usedAtWindowStart: used,
              lastToastAt: burnWindow?.lastToastAt ?? 0,
            };
            writeBurnWindow(burnWindow);
          }

          const burned = used - burnWindow.usedAtWindowStart;
          const onCooldown = now - burnWindow.lastToastAt < WINDOW_MS;
          if (
            burned < getSpendablePool(creditLimits) * BURN_TOAST_PCT ||
            onCooldown
          )
            return;

          writeBurnWindow({ ...burnWindow, lastToastAt: now });
          trackEvent('USAGE_PACE_WARNING_SHOWN', { burned });
          toast.info("You're going through your usage quickly", {
            action: {
              label: 'View usage',
              onClick: () => setHash('settings/Usage'),
            },
          });
        },
        { fireImmediately: true }
      ),
    [setHash]
  );
};
